import config from "./config";
import { formatISO } from "date-fns";
import { partition } from "ramda";

export interface SensorMeta {
  created: Date;
}

export interface LegacySensor {
  idx: number;
  source: string;
  unitLong?: string;
}

export interface SensorApiResponse<M extends SensorMeta, S extends LegacySensor> {
  meta: M;
  data: S[];
}

const filterAgencyPassList = <D extends LegacySensor>(data: D[]) => {
  const { agencyPassList } = config;
  return agencyPassList === undefined
    ? data // Assume all data if no list is specified
    : data.filter(({ source }) => {
        const normalizedSource = source.toLowerCase();
        return agencyPassList.some((agency) =>
          normalizedSource.includes(agency.toLowerCase())
        );
      });
};

const filterBadUnits = <D extends LegacySensor>(data: D[], unit: RegExp) => {
  const [good, bad] = partition(({ unitLong }) => {
    return unitLong === undefined || unit.test(unitLong as string);
  }, data);
  if (bad.length > 0) {
    console.error(`can't handle unitLong which doesn't match ${unit}`);
  }
  return good;
};

export const fetchSensorAPI = async <M extends SensorMeta, S extends LegacySensor>(
  url: string,
  check: RegExp
): Promise<SensorApiResponse<M, S>> => {
  try {
    const fetchResponse = await fetch(url);
    if (!fetchResponse.ok) {
      throw new Error(`${fetchResponse.status} ${fetchResponse.statusText}`);
    }
    const json: any = await fetchResponse.json();
    const meta = { ...json.meta, created: new Date(json.meta.created) };
    const data = filterBadUnits<S>(filterAgencyPassList<S>(json.data), check);
    return { meta, data };
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const rootApiUrl = process.env.REACT_APP_API_URL || "https://jefdaq.jefulleralert.com"

export interface TabularData {
  data: {
    local: string;
    utc: string;
    val: number;
  }[];
  meta: {
    units: string[];
  };
}

export interface TabularDataRated {
  data: {
    local: string;
    utc: string;
    val: number;
  }[];
  meta: {
    units: string[];
  };
}

export const tabularDataUrl = (
  sensorId: string,
  range?: { startDate: Date; endDate: Date },
  rated?: boolean
) => {
  const url = rootApiUrl + (rated ? "/tabular_dataRated/" : "/tabular_data/");

  const params = new URLSearchParams({ sensorPair: sensorId });
  if (range) {
    params.append(
      "startDate",
      formatISO(range.startDate, { representation: "date" })
    );
    params.append(
      "endDate",
      formatISO(range.endDate, { representation: "date" })
    );
  }
  return url + "?" + params;
};